import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useModuleVisibility } from '../../hooks/useModuleVisibility';
import { Barbell, SoccerBall } from '@phosphor-icons/react';
import ExercisesTab from '../../components/ProgrammingPage/ExercisesTab/ExercisesTab';
import BlocksTab from '../../components/ProgrammingPage/BlocksTab/BlocksTab';
import WorkoutsTab from '../../components/ProgrammingPage/WorkoutsTab/WorkoutsTab';
import FieldExercisesTab from '../../components/ProgrammingPage/FieldExercisesTab/FieldExercisesTab';
import FieldBlocksTab from '../../components/ProgrammingPage/FieldBlocksTab/FieldBlocksTab';
import FieldWorkoutsTab from '../../components/ProgrammingPage/FieldWorkoutsTab/FieldWorkoutsTab';
import styles from './ProgrammingPage.module.scss';

type Mode = 'gym' | 'field';
type Tab = 'schede' | 'blocchi' | 'esercizi';

/**
 * HIDE-CAMPO (2026-07-25): programmazione Campo temporaneamente nascosta lato UI.
 * Backend e componenti Field* intatti. Per riattivare rimettere a `true` questo
 * flag — nessun'altra modifica necessaria.
 */
const FIELD_MODE_ENABLED: boolean = false;

const TAB_LABELS: Record<Tab, string> = {
  schede: 'Schede',
  blocchi: 'Blocchi',
  esercizi: 'Esercizi',
};

/** Mappa tab-id → chiave feature-flag */
const PROGRAMMING_TAB_VISIBILITY_KEYS: Record<Tab, string> = {
  schede: 'programming-schede',
  blocchi: 'programming-blocchi',
  esercizi: 'programming-esercizi',
};

const ALL_PROGRAMMING_TABS: Tab[] = ['schede', 'blocchi', 'esercizi'];

const isMode = (v: string | null): v is Mode => v === 'gym' || v === 'field';
const isTab = (v: string | null): v is Tab =>
  v === 'schede' || v === 'blocchi' || v === 'esercizi';

const ProgrammingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isTabVisible } = useModuleVisibility();
  const modeParam = searchParams.get('mode');
  const tabParam = searchParams.get('tab');
  // HIDE-CAMPO: con flag off il mode è forzato a 'gym' anche se l'URL chiede ?mode=field
  const initialMode: Mode =
    FIELD_MODE_ENABLED && isMode(modeParam) ? modeParam : 'gym';
  const initialTab: Tab = isTab(tabParam) ? tabParam : 'schede';

  const [mode, setMode] = useState<Mode>(initialMode);
  const [tab, setTab] = useState<Tab>(initialTab);

  // Tab visibili secondo i feature-flag runtime
  const visibleTabs = useMemo(
    () => ALL_PROGRAMMING_TABS.filter((t) => isTabVisible(PROGRAMMING_TAB_VISIBILITY_KEYS[t])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTabVisible]
  );

  // Se il tab attivo viene nascosto da un flag, spostati al primo visibile
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(tab)) {
      setTab(visibleTabs[0]);
    }
  }, [visibleTabs, tab]);

  useEffect(() => {
    setSearchParams({ mode, tab }, { replace: true });
  }, [mode, tab, setSearchParams]);

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.inner}>
          <div className={styles.titleRow}>
            <div className={styles.titleLeft}>
              <div className={styles.titleBlock}>
                <p className={styles.pageTitle}>Programmazione</p>
                <p className={styles.pageSubtitle}>
                  {FIELD_MODE_ENABLED
                    ? 'Gestisci esercizi, blocchi e schede di allenamento (Palestra e Campo)'
                    : 'Gestisci esercizi, blocchi e schede di allenamento'}
                </p>
              </div>
            </div>
          </div>

          {/* HIDE-CAMPO: switcher Palestra/Campo nascosto — riattivare via FIELD_MODE_ENABLED */}
          {FIELD_MODE_ENABLED && (
          <div className={styles.modeSwitch} role="tablist">
            <button
              type="button"
              className={[
                styles.modeBtn,
                mode === 'gym' ? styles.modeBtnActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setMode('gym')}
              role="tab"
              aria-selected={mode === 'gym'}
            >
              <Barbell size={16} weight="fill" />
              Palestra
            </button>
            <button
              type="button"
              className={[
                styles.modeBtn,
                mode === 'field' ? styles.modeBtnActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setMode('field')}
              role="tab"
              aria-selected={mode === 'field'}
            >
              <SoccerBall size={16} weight="fill" />
              Campo
            </button>
          </div>
          )}

          <div className={styles.tabsRow}>
            <div className={styles.tabsLeft}>
              <div className={styles.tabGroup}>
                {visibleTabs.map((t) => {
                  const isActive = tab === t;
                  return (
                    <button
                      key={t}
                      onClick={() => setTab(t)}
                      className={[
                        styles.tabBtn,
                        isActive ? styles.tabBtnActive : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <div className={styles.tabBtnInner}>
                        <p
                          className={[
                            styles.tabLabel,
                            isActive ? styles.tabLabelActive : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {TAB_LABELS[t]}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {mode === 'gym' && tab === 'schede' && <WorkoutsTab />}
          {mode === 'gym' && tab === 'blocchi' && <BlocksTab />}
          {mode === 'gym' && tab === 'esercizi' && <ExercisesTab />}
          {mode === 'field' && tab === 'schede' && <FieldWorkoutsTab />}
          {mode === 'field' && tab === 'blocchi' && <FieldBlocksTab />}
          {mode === 'field' && tab === 'esercizi' && <FieldExercisesTab />}
        </div>
      </div>
    </div>
  );
};

export default ProgrammingPage;
